/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useState } from "react";
import { FormControl, FormHelperText, InputLabel } from "@mui/material";
import clsx from "clsx";
import { environment } from "../../environment";
import useErrors from "../../Utils/userErrors";
import withTooltip from "../HOC/withTooltip";
import InputContainerModel from "./InputContainerModel";
import InputContainerProps from "./InputContainerProps";
import variablesScss from "../../Themes/variables.module.scss";

export default withTooltip(function InputContainer(props: InputContainerProps) {
    const { error, errorTexts, clear } = useErrors(props);
    const vm = new InputContainerModel(props);
    const { errorResetFlag } = props;
    const [isFocused, setIsFocused] = useState(false);

    React.useEffect(() => {
        if (typeof errorResetFlag !== "undefined") {
            clear();
        }
    }, [errorResetFlag, clear]);

    let formControlClassNames = vm.getFormControlClassNames(error);
    if (props.readonly) {
        formControlClassNames += " disabled";
    }

    return (
        <div className={clsx("lan-inputcontainer", props.readonly && "lan-inputcontainer--readonly")} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
            <FormControl
                className={formControlClassNames}
                disabled={props.readonly}
                error={error}
                fullWidth={props.fullWidth}
                required={props.required}
                margin={props.margin}
                variant={props.variant}
                sx={[
                    {
                        margin: "8px 0px",
                        border: `1px solid`,
                        position: "relative",
                        borderRadius: theme => typeof theme.shape.borderRadius === "string" ? theme.shape.borderRadius : `${theme.shape.borderRadius}px`,
                        borderColor: theme => error ? `${theme.palette.error.main} !important` : variablesScss.colorsDefaultBorder,
                        "&:hover": {
                            borderColor: theme => props.disableBorderHover ? "none" : theme.palette.text.primary,
                        },
                    },
                    (!!props.isFocused || isFocused) && !props.disableFocus && !error && {
                        outline: theme => `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: "-1px",
                    },
                    {
                        "&.disabled": {
                            "&:hover": {
                                borderColor: `${variablesScss.colorsDisabledBorder} !important`,
                            },
                        },
                    }
                ]}
            >
                <InputLabel
                    error={error}
                    disabled={props.readonly}
                    {...vm.getInputLabelProps(error)}
                    shrink
                    sx={[
                        {
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: 2,
                            pointerEvents: "none",
                            color: theme => error ? `${theme.palette.error.main} !important` : environment.istBuchungsportal ? `${theme.palette.text}` : variablesScss.colorsFeldueberschrift,
                            padding: "0 6px",
                            backgroundColor: "white",
                        },
                        (!!props.isFocused || isFocused) && {
                            color: theme => theme.palette.primary.main,
                        },
                    ]}
                >
                    {props.caption}
                </InputLabel>
                {props.children}
            </FormControl>
            <FormHelperText error={error} {...vm.formHelperTextProps}>
                {errorTexts.join(" ")}
            </FormHelperText>
        </div>
    );
});
